<template>
    <TransitionRoot as="template" :show="showing">
        <Dialog as="div" class="fixed z-50 inset-0 overflow-y-auto" @close="emit('close')">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <div class="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                        <div>
                            <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
                                <BugAntIcon class="h-6 w-6 text-gray-600" aria-hidden="true" />
                            </div>
                            <div class="mt-3 text-center sm:mt-5">
                                <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">Account Debug Information</DialogTitle>
                                <div class="mt-4">
                                    <div class="p-6 bg-gray-50 border border-gray-100 rounded-md">
                                        <p class="text-sm font-mono text-left my-0">
                                            <template v-for="(value, key) in debugInfo">
                                                {{ key }}: {{ value }}<br />
                                            </template>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex gap-4 mt-5 sm:mt-6">
                            <button type="button" class="inline-flex justify-center w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" @click="copyToClipboard">Copy to Clipboard</button>
                            <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm" @click="emit('close')">Return</button>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import {toRef, computed} from "vue"
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from "@headlessui/vue"
import {CheckIcon, XMarkIcon} from "@heroicons/vue/24/outline"
import {BugAntIcon} from "@heroicons/vue/24/solid";
import {formatDateShort} from "../../../../dateformats";
import {useBus} from "../../../../app";

export default {
    name: "Debug",
    components: {
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        BugAntIcon,
        CheckIcon,
        XMarkIcon,
    },
    emits: [
        'close'
    ],
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        user: {
            type: Object,
            default: {},
        }
    },
    setup(props, { emit }) {
        const bus = useBus()
        const showing = toRef(props, 'open')

        const debugInfo = computed(() => {
            return removeEmptyValues({
                'User ID': props.user.id,
                'Account ID': props.user.account_id,
                'Subscription': props.user.account?.subscription?.id ?
                    props.user.account?.subscription?.id + ' (' + props.user.account?.subscription?.payment_ref + ')' :
                    '',
                'Subscription Expiry': props.user.account?.subscription?.expires ?
                    formatDateShort(props.user.account?.subscription?.expires) :
                    '',
                'Subscription Cancelled': props.user.account?.subscription?.cancelled ?
                    formatDateShort(props.user.account?.subscription?.cancelled) :
                    '',
            })
        })

        const removeEmptyValues = (obj) => {
            return Object.entries(obj).reduce((acc, [key, value]) => {
                if (value !== null && value !== "" && typeof value !== "undefined") {
                    if (typeof value === "object") {
                        const subObj = removeEmptyValues(value);
                        if (Object.keys(subObj).length !== 0) {
                            acc[key] = subObj
                        }
                    } else {
                        acc[key] = value
                    }
                }
                return acc;
            }, {})
        }

        const copyToClipboard = () => {
            let clipboardText = '\n'
            for (const [key, value] of Object.entries(debugInfo.value)) {
                clipboardText += key + ': ' + value + '\n'
            }

            window.navigator.clipboard.writeText(clipboardText)
                .then(() => {
                    bus.$emit('alert.success', 'Text has been copied to the clipboard')
                })
                .catch((error) => {
                    bus.$emit('alert.danger', 'Error copying text')
                });
        }

        return {
            emit,
            showing,
            debugInfo,
            copyToClipboard,
        }
    }
}
</script>

<style scoped>

</style>
